// Issue #91, #92, #93, #121: Chat list page for message tab
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './ChatList.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, ConfirmationModal } from './shared';
import { useLogout, useUserData } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function ChatList() {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();

  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  // Issue #121: Long-press and hide chat states
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showOptionsPopover, setShowOptionsPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const longPressTimer = useRef(null);
  const popoverRef = useRef(null);
  const LONG_PRESS_DURATION = 500; // 500ms for responsive long-press

  // Issue #122: Block user states
  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);

  // Issue #173: Mute notification states
  const [isMuting, setIsMuting] = useState(false);

  // Initial fetch and polling for new conversations
  useEffect(() => {
    const fetchConversations = async (isInitialLoad = false) => {
      if (isInitialLoad) {
        setIsLoading(true);
      }
      try {
        const response = await axios.get(`${API_BASE_URL}/api/conversations`);
        setConversations(response.data);
      } catch (err) {
        console.error('Failed to fetch conversations:', err);
        if (isInitialLoad) {
          setError(err.response?.data?.message || t('chatList.errorLoading'));
        }
      } finally {
        if (isInitialLoad) {
          setIsLoading(false);
        }
      }
    };

    // Initial load
    fetchConversations(true);

    // Poll for new conversations every 3 seconds
    const pollInterval = setInterval(() => {
      fetchConversations(false);
    }, 3000);

    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  }, [t]);

  // Format timestamp with relative time (like notification card)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Show relative time for recent messages
    if (diffMins < 1) {
      return t('notifications.justNow');
    } else if (diffMins < 60) {
      return `${diffMins} ${t('notifications.minutesAgo')}`;
    } else if (diffHours < 24) {
      return `${diffHours} ${t('notifications.hoursAgo')}`;
    } else if (diffDays < 7) {
      return `${diffDays} ${t('notifications.daysAgo')}`;
    }

    // Show date for older messages
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Truncate message preview to 50 characters
  const truncatePreview = (message) => {
    if (!message) return '';
    if (message.length <= 50) return message;
    return message.substring(0, 50) + '...';
  };

  const handleConversationClick = (conversationId) => {
    // Issue #121: Only navigate if not showing popover
    if (!showOptionsPopover) {
      navigation.onNavigateToChat(conversationId);
    }
  };

  // Issue #121: Long-press handlers
  const handleLongPressStart = useCallback((event, conversation) => {
    // Prevent default touch behavior (native context menu, text selection)
    if (event.type === 'touchstart') {
      event.preventDefault();
    }

    // Clear any existing timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }

    // Get position for popover - align to right edge, position at bottom of card
    const rect = event.currentTarget.getBoundingClientRect();
    const x = rect.right - 180; // 180px is popover width
    const y = rect.bottom;

    longPressTimer.current = setTimeout(() => {
      setSelectedConversation(conversation);
      setPopoverPosition({ x, y });
      setShowOptionsPopover(true);
    }, LONG_PRESS_DURATION);
  }, []);

  // Issue #122: Keyboard handler for accessibility (WCAG 2.1.1)
  // Opens options popover with Shift+Enter or Context Menu key
  const handleCardKeyDown = useCallback((event, conversation) => {
    // Shift+Enter or Context Menu key opens options popover
    if ((event.shiftKey && event.key === 'Enter') || event.key === 'ContextMenu') {
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      const x = rect.right - 180; // 180px is popover width
      const y = rect.bottom;
      setSelectedConversation(conversation);
      setPopoverPosition({ x, y });
      setShowOptionsPopover(true);
    }
  }, []);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleCloseOptionsPopover = useCallback(() => {
    setShowOptionsPopover(false);
    setSelectedConversation(null);
  }, []);

  const handleDeleteChatClick = useCallback(() => {
    setShowOptionsPopover(false);
    setShowDeleteConfirmation(true);
  }, []);

  const handleConfirmDeleteChat = useCallback(async () => {
    if (!selectedConversation) return;

    setIsDeleting(true);
    try {
      await axios.put(`${API_BASE_URL}/api/conversations/${selectedConversation.id}/hide`);
      // Remove the conversation from the list
      setConversations(prev => prev.filter(c => c.id !== selectedConversation.id));
      setShowDeleteConfirmation(false);
      setSelectedConversation(null);
    } catch (err) {
      console.error('Failed to hide conversation:', err);
      alert(err.response?.data?.message || 'Fehler beim Löschen des Chats');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedConversation]);

  const handleCancelDeleteChat = useCallback(() => {
    setShowDeleteConfirmation(false);
    setSelectedConversation(null);
  }, []);

  // Issue #122: Block user handlers
  const handleBlockUserClick = useCallback(() => {
    setShowOptionsPopover(false);
    setShowBlockConfirmation(true);
  }, []);

  const handleConfirmBlockUser = useCallback(async () => {
    if (!selectedConversation || !selectedConversation.other_user_id) return;

    setIsBlocking(true);
    try {
      await axios.post(`${API_BASE_URL}/api/users/${selectedConversation.other_user_id}/block`);
      // Remove the conversation from the list and clear all state
      setConversations(prev => prev.filter(c => c.id !== selectedConversation.id));
      setShowBlockConfirmation(false);
      setSelectedConversation(null);
    } catch (err) {
      console.error('Failed to block user:', err);
      alert(err.response?.data?.message || 'Fehler beim Blockieren des Benutzers');
    } finally {
      setIsBlocking(false);
    }
  }, [selectedConversation]);

  const handleCancelBlockUser = useCallback(() => {
    setShowBlockConfirmation(false);
    setSelectedConversation(null);
  }, []);

  // Issue #173: Toggle mute notification for conversation
  const handleToggleMuteClick = useCallback(async () => {
    if (!selectedConversation) return;

    setIsMuting(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/api/conversations/${selectedConversation.id}/mute`);
      // Update the conversation's muted status in the list
      setConversations(prev => prev.map(c =>
        c.id === selectedConversation.id
          ? { ...c, is_muted: response.data.is_muted }
          : c
      ));
      setShowOptionsPopover(false);
      setSelectedConversation(null);
    } catch (err) {
      console.error('Failed to toggle mute:', err);
      alert(err.response?.data?.message || t('chatList.muteError'));
    } finally {
      setIsMuting(false);
    }
  }, [selectedConversation, t]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptionsPopover && !event.target.closest('.chat-options-popover')) {
        handleCloseOptionsPopover();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showOptionsPopover, handleCloseOptionsPopover]);

  // Issue #122: Focus management for popover accessibility (WCAG 2.4.3)
  useEffect(() => {
    if (showOptionsPopover && popoverRef.current) {
      // Focus the first button in the popover
      const firstButton = popoverRef.current.querySelector('button');
      if (firstButton) {
        setTimeout(() => firstButton.focus(), 10);
      }
    }
  }, [showOptionsPopover]);

  // Issue #122: Keyboard navigation within popover (WCAG 2.1.1)
  const handlePopoverKeyDown = useCallback((event) => {
    if (!showOptionsPopover || !popoverRef.current) return;

    const buttons = popoverRef.current.querySelectorAll('button');
    const currentIndex = Array.from(buttons).indexOf(document.activeElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        handleCloseOptionsPopover();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (currentIndex < buttons.length - 1) {
          buttons[currentIndex + 1].focus();
        } else {
          buttons[0].focus(); // Wrap to first
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (currentIndex > 0) {
          buttons[currentIndex - 1].focus();
        } else {
          buttons[buttons.length - 1].focus(); // Wrap to last
        }
        break;
      case 'Tab':
        // Focus trap within popover
        if (event.shiftKey && currentIndex === 0) {
          event.preventDefault();
          buttons[buttons.length - 1].focus();
        } else if (!event.shiftKey && currentIndex === buttons.length - 1) {
          event.preventDefault();
          buttons[0].focus();
        }
        break;
      default:
        break;
    }
  }, [showOptionsPopover, handleCloseOptionsPopover]);

  if (isLoading) {
    return (
      <div className="chat-list-page">
        <AppHeader
          userData={userData}
          onLogout={handleLogout}
          isLoggingOut={isLoggingOut}
          logoutError={logoutError}
          onClearLogoutError={clearLogoutError}
        />
        <div className="tab-navigation-chat-list">
          <span className="tab-title">{t('chatList.title')}</span>
        </div>
        <main className="chat-list-content">
          <div className="loading-container">
            <span>{t('chatList.loading')}</span>
          </div>
        </main>
        <BottomNavigation
          activeTab="messages"
          onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
        />
      </div>
    );
  }

  return (
    <div className="chat-list-page">
      {/* Header Navigation Bar - Using shared AppHeader component */}
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation-chat-list">
        <span className="tab-title">{t('chatList.title')}</span>
      </div>

      {/* Content */}
      <main className="chat-list-content">
        {/* Issue #115: Added role="alert" for screen reader announcement */}
        {error && (
          <div className="error-message" role="alert" aria-live="assertive">
            {error}
          </div>
        )}

        {/* Issue #115: Added aria-live region for dynamic content */}
        {conversations.length === 0 && !error ? (
          <div className="empty-state" role="status">
            <svg className="empty-state-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="empty-title">{t('chatList.noConversations')}</span>
            <span className="empty-hint">{t('chatList.noConversationsHint')}</span>
          </div>
        ) : (
          <div className="conversations-list" role="list" aria-label={t('chatList.title') || 'Nachrichten'} aria-live="polite">
            {conversations.map((conversation) => (
              <button
                key={conversation.id}
                className="conversation-card"
                onClick={() => handleConversationClick(conversation.id)}
                onMouseDown={(e) => handleLongPressStart(e, conversation)}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressEnd}
                onTouchStart={(e) => handleLongPressStart(e, conversation)}
                onTouchEnd={handleLongPressEnd}
                onTouchCancel={handleLongPressEnd}
                onKeyDown={(e) => handleCardKeyDown(e, conversation)}
                aria-label={`${conversation.is_muted ? (t('chatList.muted') || 'Stumm') + '. ' : ''}${conversation.unread_count > 0 ? (t('chatList.unread') || 'Ungelesen') + ': ' : ''}${conversation.marketplace_object_title ? conversation.marketplace_object_title + '. ' : ''}${t('chatList.conversationWith') || 'Unterhaltung mit'} ${conversation.other_user_name} - ${truncatePreview(conversation.last_message)}. ${t('chatList.optionsHint') || 'Shift+Enter für Optionen'}`}
              >
                {/* Profile Icon/Photo with Unread Indicator - styled like notification icons */}
                <div className="profile-icon-container" aria-hidden="true">
                  {conversation.other_user_profile_photo_url ? (
                    <img
                      src={`${API_BASE_URL}${conversation.other_user_profile_photo_url}`}
                      alt=""
                      className="chat-profile-photo"
                    />
                  ) : (
                    <svg className="profile-icon chat-profile-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                  {/* Issue #93: Red dot for unread messages */}
                  {conversation.unread_count > 0 && (
                    <div className="unread-dot" aria-hidden="true"></div>
                  )}
                </div>

                {/* Conversation Info - structured like notification card */}
                <div className="conversation-info">
                  {conversation.marketplace_object_title && (
                    <span className="marketplace-object-title">
                      {conversation.marketplace_object_title}
                    </span>
                  )}
                  <span className="other-user-name">{conversation.other_user_name}</span>
                  <span className="message-preview">
                    {truncatePreview(conversation.last_message)}
                  </span>
                  <span className="conversation-timestamp">
                    {formatTimestamp(conversation.last_message_at)}
                  </span>
                </div>

                {/* Issue #173: Muted notification icon and Issue #93: Unread Count Badge */}
                <div className="chat-card-right" aria-hidden="true">
                  {conversation.is_muted && (
                    <svg
                      className="muted-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-label={t('chatList.mutedNotification') || 'Benachrichtigungen stumm'}
                    >
                      <path d="M13.73 21C13.5542 21.3031 13.3018 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                  {conversation.unread_count > 0 && (
                    <div className="unread-badge">
                      {conversation.unread_count}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="messages"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Create Options Menu - Using shared component with global modal */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />

      {/* Issue #121: Chat Options Popover - Issue #122: Full keyboard accessibility */}
      {showOptionsPopover && selectedConversation && (
        <div className="chat-options-overlay" onClick={handleCloseOptionsPopover} role="presentation">
          <div
            ref={popoverRef}
            className="chat-options-popover"
            style={{
              top: `${popoverPosition.y}px`,
              left: `${Math.min(popoverPosition.x, window.innerWidth - 200)}px`,
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handlePopoverKeyDown}
            role="menu"
            aria-label={t('chatList.optionsMenu') || 'Chat-Optionen'}
          >
            {/* Issue #173: Mute/Unmute notification option */}
            <button
              className="chat-option-item"
              onClick={handleToggleMuteClick}
              disabled={isMuting}
              role="menuitem"
              aria-busy={isMuting}
            >
              {isMuting
                ? (t('chatList.mutingNotification') || 'Wird geändert...')
                : (selectedConversation.is_muted
                    ? (t('chatList.unmuteNotification') || 'Notifikation einschalten')
                    : (t('chatList.muteNotification') || 'Notifikation ausschalten')
                  )
              }
            </button>
            <button
              className="chat-option-item"
              onClick={handleDeleteChatClick}
              role="menuitem"
            >
              {t('chatList.deleteChat')}
            </button>
            <button
              className="chat-option-item chat-option-block"
              onClick={handleBlockUserClick}
              role="menuitem"
            >
              {(t('chatList.blockUser') || '{name} blockieren').replace('{name}', selectedConversation.other_user_name)}
            </button>
          </div>
        </div>
      )}

      {/* Issue #121: Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={handleCancelDeleteChat}
        onConfirm={handleConfirmDeleteChat}
        title={t('chatList.deleteChatTitle')}
        message={(t('chatList.deleteChatMessage') || 'Der Chat wird aus deiner Liste entfernt. Wenn {name} dir erneut schreibt, erscheint der Chat wieder.').replace('{name}', selectedConversation?.other_user_name || '')}
        confirmText={t('chatList.deleteChatConfirm')}
        confirmingText={t('chatList.deleteChatConfirming')}
        isConfirming={isDeleting}
        variant="danger"
      />

      {/* Issue #122: Block User Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBlockConfirmation}
        onClose={handleCancelBlockUser}
        onConfirm={handleConfirmBlockUser}
        title={(t('chatList.blockUserTitle') || 'Möchtest du {name} wirklich blockieren?').replace('{name}', selectedConversation?.other_user_name || '')}
        message={(t('chatList.blockUserMessage') || '{name} wird dich nicht mehr kontaktieren können. Du wirst keine Nachrichten mehr von {name} erhalten.').replace(/{name}/g, selectedConversation?.other_user_name || '')}
        confirmText={(t('chatList.blockUserConfirm') || '{name} blockieren').replace('{name}', selectedConversation?.other_user_name || '')}
        confirmingText={t('chatList.blockUserConfirming') || 'Blockieren...'}
        cancelText={t('chatList.blockUserCancel') || 'Abbrechen'}
        isConfirming={isBlocking}
        variant="danger"
      />
    </div>
  );
}

export default ChatList;
